import { buildUniqueSelector } from './selector-utils';

export class AriaHiddenInteractiveRule {
	constructor(i18n) {
		this.__ = i18n;
		this.key = 'ariaHiddenInteractive';
	}

	getDefaultStatus() {
		return 'not_checked';
	}

	evaluate(documentNode, pageUrl = '') {
		const nodes = this.getCandidateNodes(documentNode);
		const elements = nodes.map((node, index) => this.buildElementInfo(node, index, pageUrl));
		const issues = elements.filter((element) => element.issue.length > 0);

		if (elements.length === 0) {
			return {
				status: 'pass',
				message: this.__( 'No interactive candidates found for aria-hidden checks.', 'a11yfix' ),
				details: {
					elements,
					issues
				}
			};
		}

		if (issues.length > 0) {
			return {
				status: 'fail',
				message: `${this.__( 'Interactive aria-hidden issues found:', 'a11yfix' )} ${issues.length}`,
				details: {
					elements,
					issues
				}
			};
		}

		return {
			status: 'pass',
			message: this.__( 'No interactive content is hidden with aria-hidden.', 'a11yfix' ),
			details: {
				elements,
				issues
			}
		};
	}

	getCandidateNodes(documentNode) {
		const selector = [
			'a[href]',
			'area[href]',
			'button',
			'input:not([type="hidden"])',
			'select',
			'textarea',
			'summary',
			'iframe',
			'audio[controls]',
			'video[controls]',
			'[contenteditable]:not([contenteditable="false"])',
			'[tabindex]',
			'[role="button"]',
			'[role="link"]',
			'[role="checkbox"]',
			'[role="radio"]',
			'[role="switch"]',
			'[role="tab"]',
			'[role="menuitem"]',
			'[role="textbox"]'
		].join(', ');

		return Array.from(documentNode.querySelectorAll(selector)).filter((node) => {
			if (node.closest('#wpadminbar')) {
				return false;
			}

			return !this.isDisabled(node);
		});
	}

	buildElementInfo(node, index, pageUrl) {
		const selfAriaHidden = String(node.getAttribute('aria-hidden') || '').toLowerCase() === 'true';
		const hiddenAncestor = node.parentElement ? node.parentElement.closest('[aria-hidden="true"]') : null;
		let issue = '';

		if (selfAriaHidden) {
			issue = this.__( 'Interactive element has aria-hidden="true".', 'a11yfix' );
		} else if (hiddenAncestor) {
			issue = this.__( 'Interactive element is inside an aria-hidden="true" container.', 'a11yfix' );
		}

		return {
			index: index + 1,
			url: pageUrl,
			selector: buildUniqueSelector(node),
			tag: node.tagName.toLowerCase(),
			id: node.getAttribute('id') || '',
			name: node.getAttribute('name') || '',
			role: node.getAttribute('role') || '',
			ariaHidden: node.getAttribute('aria-hidden') || '',
			hasHiddenAncestor: Boolean(hiddenAncestor),
			hiddenAncestorSelector: hiddenAncestor ? buildUniqueSelector(hiddenAncestor) : '',
			issue
		};
	}

	isDisabled(node) {
		if ('disabled' in node && node.disabled) {
			return true;
		}

		const ariaDisabled = String(node.getAttribute('aria-disabled') || '').toLowerCase();
		if (ariaDisabled === 'true') {
			return true;
		}

		if (node.closest('fieldset[disabled]')) {
			return true;
		}

		return false;
	}
}
